import React, { memo } from "react";
import styled from "styled-components";
import { mergeClassNames } from "@flight-digital/flightdeck/helpers";

interface Props extends React.SVGProps<SVGSVGElement> {
  color?: string;
  size?: number;
  asset:
    | "arrowBlue"
    | "playButton"
    | "submitArrow"
    | "fb"
    | "insta"
    | "youtube"
    | "close"
    | "edit"
    | "delete"
    | "tick"
    | "tooltip"
    | "removePhoto";
}

const Icon = memo(
  ({
    asset,
    color = "currentColor",
    onClick,
    className,
    size,
    ...rest
  }: Props) => {
    const mainColor = color;

    const Svg = (props: React.SVGProps<SVGSVGElement>) => (
      <StyledSvg
        clickable={Boolean(onClick)}
        onClick={onClick}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={mergeClassNames("icon", asset, className)}
        role={onClick ? "button" : "none"}
        {...props}
        {...rest}
        {...(size
          ? {
              width: size,
              height: size,
              style: { ...(rest.style ?? {}), minWidth: `${size}px` },
            }
          : {})}
      />
    );

    if (asset === "arrowBlue") {
      return (
        <Svg
          width="24"
          height="11"
          viewBox="0 0 24 11"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          stroke={mainColor}
        >
          <path d="M0 5.59961L23 5.59961" />
          <path d="M18.3999 1L22.9999 5.6L18.3999 10.2" />
        </Svg>
      );
    }

    if (asset === "playButton") {
      return (
        <Svg
          width="60"
          height="60"
          viewBox="0 0 60 60"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M40.4162 28.6248L24.4837 19.8199C23.8172 19.4516 23 19.9337 23 20.6952L23 38.3048C23 39.0663 23.8172 39.5484 24.4837 39.1801L40.4162 30.3752C41.1046 29.9948 41.1046 29.0052 40.4162 28.6248Z"
            fill="white"
          />
          <circle
            cx="30"
            cy="30"
            r="29.5"
            stroke="white"
            stroke-opacity="0.4"
          />
        </Svg>
      );
    }

    if (asset === "submitArrow") {
      return (
        <Svg
          width="24"
          height="10"
          viewBox="0 0 24 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M0 5L23 5" stroke="white" />
          <path
            d="M18.4004 0.400391L23.0004 5.00039L18.4004 9.60039"
            stroke="white"
          />
        </Svg>
      );
    }

    if (asset === "fb") {
      return (
        <Svg
          version="1.1"
          id="Layer_1"
          xmlns="http://www.w3.org/2000/svg"
          x="0px"
          y="0px"
          viewBox="0 0 504 504"
          stroke="white"
        >
          <g>
            <g>
              <path
                d="M377.6,0H126C56.8,0,0,56.8,0,126.4V378c0,69.2,56.8,126,126,126h251.6c69.6,0,126.4-56.8,126.4-126.4V126.4
         C504,56.8,447.2,0,377.6,0z M319.6,252H272v156h-60V252h-32v-64h28v-27.2c0-25.6,12.8-66,66.8-66H324V148h-34.8
         c-5.6,0-13.2,3.6-13.2,16v24h49.2L319.6,252z"
                stroke="white"
              />
            </g>
          </g>
        </Svg>
      );
    }

    if (asset === "insta") {
      return (
        <svg
          viewBox="0 0 22 22"
          xmlns="http://www.w3.org/2000/svg"
          width="22"
          height="22"
        >
          <path d="m510.949219 150.5c-1.199219-27.199219-5.597657-45.898438-11.898438-62.101562-6.5-17.199219-16.5-32.597657-29.601562-45.398438-12.800781-13-28.300781-23.101562-45.300781-29.5-16.296876-6.300781-34.898438-10.699219-62.097657-11.898438-27.402343-1.300781-36.101562-1.601562-105.601562-1.601562s-78.199219.300781-105.5 1.5c-27.199219 1.199219-45.898438 5.601562-62.097657 11.898438-17.203124 6.5-32.601562 16.5-45.402343 29.601562-13 12.800781-23.097657 28.300781-29.5 45.300781-6.300781 16.300781-10.699219 34.898438-11.898438 62.097657-1.300781 27.402343-1.601562 36.101562-1.601562 105.601562s.300781 78.199219 1.5 105.5c1.199219 27.199219 5.601562 45.898438 11.902343 62.101562 6.5 17.199219 16.597657 32.597657 29.597657 45.398438 12.800781 13 28.300781 23.101562 45.300781 29.5 16.300781 6.300781 34.898438 10.699219 62.101562 11.898438 27.296876 1.203124 36 1.5 105.5 1.5s78.199219-.296876 105.5-1.5c27.199219-1.199219 45.898438-5.597657 62.097657-11.898438 34.402343-13.300781 61.601562-40.5 74.902343-74.898438 6.296876-16.300781 10.699219-34.902343 11.898438-62.101562 1.199219-27.300781 1.5-36 1.5-105.5s-.101562-78.199219-1.300781-105.5zm-46.097657 209c-1.101562 25-5.300781 38.5-8.800781 47.5-8.601562 22.300781-26.300781 40-48.601562 48.601562-9 3.5-22.597657 7.699219-47.5 8.796876-27 1.203124-35.097657 1.5-103.398438 1.5s-76.5-.296876-103.402343-1.5c-25-1.097657-38.5-5.296876-47.5-8.796876-11.097657-4.101562-21.199219-10.601562-29.398438-19.101562-8.5-8.300781-15-18.300781-19.101562-29.398438-3.5-9-7.699219-22.601562-8.796876-47.5-1.203124-27-1.5-35.101562-1.5-103.402343s.296876-76.5 1.5-103.398438c1.097657-25 5.296876-38.5 8.796876-47.5 4.101562-11.101562 10.601562-21.199219 19.203124-29.402343 8.296876-8.5 18.296876-15 29.398438-19.097657 9-3.5 22.601562-7.699219 47.5-8.800781 27-1.199219 35.101562-1.5 103.398438-1.5 68.402343 0 76.5.300781 103.402343 1.5 25 1.101562 38.5 5.300781 47.5 8.800781 11.097657 4.097657 21.199219 10.597657 29.398438 19.097657 8.5 8.300781 15 18.300781 19.101562 29.402343 3.5 9 7.699219 22.597657 8.800781 47.5 1.199219 27 1.5 35.097657 1.5 103.398438s-.300781 76.300781-1.5 103.300781zm0 0" />
          <path d="m256.449219 124.5c-72.597657 0-131.5 58.898438-131.5 131.5s58.902343 131.5 131.5 131.5c72.601562 0 131.5-58.898438 131.5-131.5s-58.898438-131.5-131.5-131.5zm0 216.800781c-47.097657 0-85.300781-38.199219-85.300781-85.300781s38.203124-85.300781 85.300781-85.300781c47.101562 0 85.300781 38.199219 85.300781 85.300781s-38.199219 85.300781-85.300781 85.300781zm0 0" />
          <path d="m423.851562 119.300781c0 16.953125-13.746093 30.699219-30.703124 30.699219-16.953126 0-30.699219-13.746094-30.699219-30.699219 0-16.957031 13.746093-30.699219 30.699219-30.699219 16.957031 0 30.703124 13.742188 30.703124 30.699219zm0 0" />
        </svg>
      );
    }

    if (asset == "youtube") {
      return (
        <svg
          version="1.1"
          id="Layer_1"
          xmlns="http://www.w3.org/2000/svg"
          x="0px"
          y="0px"
          viewBox="0 0 22 22"
        >
          <g>
            <g>
              <polygon points="219.6,202.4 219.6,294.4 304.4,248.8 		" />
            </g>
          </g>
          <g>
            <g>
              <path
                d="M377.6,0H126C56.8,0,0,56.8,0,126.4V378c0,69.2,56.8,126,126,126h251.6c69.6,0,126.4-56.8,126.4-126.4V126.4
         C504,56.8,447.2,0,377.6,0z M408,264.4c0,26.4-2.4,53.2-2.4,53.2s-2.8,22.4-12,32.4c-12,13.2-25.2,13.2-31.2,14
         c-44,3.2-110,3.6-110,3.6s-82-1.2-107.2-3.6c-6.8-1.2-22.8-0.8-34.8-14c-9.6-10-12-32.4-12-32.4S96,290.8,96,264.4v-24.8
         c0-26.4,2.4-53.2,2.4-53.2s2.8-22.4,12-32.4c12-13.2,25.2-13.6,31.2-14.4C186,136.4,252,136,252,136s66,0.4,110,3.6
         c6,0.8,19.6,1.2,31.6,14c9.6,10,12,32.8,12,32.8s2.4,26.8,2.4,53.2V264.4z"
              />
            </g>
          </g>
        </svg>
      );
    }

    return null;
  },
  (prev, next) =>
    prev["aria-expanded"] === next["aria-expanded"] &&
    prev.size === next.size &&
    prev.asset === next.asset &&
    prev.color === next.color
);

export default Icon;

const StyledSvg = styled.svg<{ clickable?: boolean }>`
  user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  cursor: ${({ clickable }) => (clickable ? "pointer" : "inherit")};
`;

interface HamburgerProps extends React.HTMLAttributes<HTMLDivElement> {
  open: boolean;
}

export const Hamburger = ({ open, className, ...rest }: HamburgerProps) => {
  return (
    <StyledHamburger
      {...rest}
      className={`hamburger-icon ${open ? "open" : ""} ${className ?? ""}`}
    >
      {open ? "Close main menu" : "Open main menu"}
      <span></span>
      <span></span>
      <span></span>
    </StyledHamburger>
  );
};

const StyledHamburger = styled.div`
  width: 25px;
  height: 21px;
  font-size: 0;
  line-height: 0;

  span {
    display: block;
    width: 100%;
    height: 3px;
    margin-bottom: 6px;
    position: relative;
    background-color: var(--color-black);
    transform-origin: right;
    transition: transform 0.5s cubic-bezier(0.77, 0.2, 0.05, 1),
      background 0.3s cubic-bezier(0.77, 0.2, 0.05, 1), opacity 300ms;

    &:first-child {
      transform-origin: 100% 0%;
    }

    &:nth-last-child(1) {
      transform-origin: 100% 100%;
    }
  }

  &.open {
    span {
      opacity: 1;
      transform: rotate(-45deg) translate(-4px, -3px);

      &:nth-last-child(2) {
        opacity: 0;
        transform: rotate(0deg) scale(0.2, 0.2);
      }
      &:nth-last-child(1) {
        transform: rotate(45deg) translate(-4px, 3px);
      }
    }
  }
`;
